import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef, useMemo } from "react";
import { toast } from "sonner";
import { employeesApi } from "../api/employees";
import { externalAgentsApi } from "../api/externalAgents";
import type { Employee, ExternalAgent, ExternalMessagingUser } from "../modules/employees/types";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@/components/ui/empty";
import EmployeeCard from "./EmployeeCard";

type EmployeeScoringState = {
  employeeId: string;
  managerId: string | null;
  included: boolean;
  notifyManager: boolean;
  externalAgentId: string | null;
  externalMessagingId: string | null;
};

export interface EmployeeListHandle {
  save: () => Promise<void>;
  isDirty: () => boolean;
}

interface EmployeeListProps {
  hideRefresh?: boolean;
  onIncludedCountChange?: (count: number) => void;
  onDirtyChange?: (dirty: boolean) => void;
  /** Called after a successful internal save. When provided, a save button is rendered. */
  onSave?: () => void;
  /** Called when the user clicks the back button. When provided, a back button is rendered. */
  onBack?: () => void;
  /** Label for the save button. Defaults to "Save". */
  saveLabel?: string;
}

function EmployeeListHeader() {
  return (
    <div className="grid grid-cols-[auto_1fr_1.5fr_1.5fr_auto_1fr_auto] items-center px-6 gap-4 pb-2">
      <Text as="div" variant="xs" className="w-16 text-muted-foreground uppercase tracking-wide">
        Include
      </Text>
      <Text as="div" variant="xs" className="text-muted-foreground uppercase tracking-wide">
        Employee
      </Text>
      <Text as="div" variant="xs" className="text-muted-foreground uppercase tracking-wide">
        Intercom
      </Text>
      <Text as="div" variant="xs" className="text-muted-foreground uppercase tracking-wide">
        Slack
      </Text>
      <div className="w-px" />
      <Text as="div" variant="xs" className="text-muted-foreground uppercase tracking-wide">
        Manager
      </Text>
      <div className="w-24" />
    </div>
  );
}

function EmployeeCardSkeleton() {
  return (
    <Card className="py-4">
      <div className="grid grid-cols-[auto_1fr_1.5fr_1.5fr_auto_1fr_auto] items-center px-6 gap-4">
        <div className="flex justify-center">
          <Skeleton className="h-7 w-12 rounded-full" />
        </div>
        <div className="min-w-0">
          <Skeleton className="h-5 w-32 mb-1" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="min-w-0">
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="min-w-0">
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="w-px self-stretch bg-border" />
        <div className="min-w-0">
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="flex justify-center">
          <Skeleton className="h-7 w-12 rounded-full" />
        </div>
      </div>
    </Card>
  );
}

const EmployeeList = forwardRef<EmployeeListHandle, EmployeeListProps>(({ hideRefresh = false, onIncludedCountChange, onDirtyChange, onSave, onBack, saveLabel = "Save" }, ref) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [scoringState, setScoringState] = useState<EmployeeScoringState[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalAgents, setExternalAgents] = useState<ExternalAgent[]>([]);
  const [messagingUsers, setMessagingUsers] = useState<ExternalMessagingUser[]>([]);
  const [externalLoading, setExternalLoading] = useState(true);

  const initializeScoringState = useCallback((employeeList: Employee[]) => {
    setScoringState(
      employeeList.map((emp) => ({
        employeeId: emp.id,
        managerId: emp.managerId,
        included: emp.isScoring,
        notifyManager: emp.notifyManager,
        externalAgentId: emp.externalAgentId,
        externalMessagingId: emp.externalMessagingId,
      }))
    );
  }, []);

  const getChangedEmployees = useCallback(() => {
    return scoringState.filter((state) => {
      const employee = employees.find((emp) => emp.id === state.employeeId);
      if (!employee) return false;
      return (
        state.managerId !== employee.managerId ||
        state.included !== employee.isScoring ||
        state.notifyManager !== employee.notifyManager ||
        state.externalAgentId !== employee.externalAgentId ||
        state.externalMessagingId !== employee.externalMessagingId
      );
    });
  }, [scoringState, employees]);

  const isDirty = useCallback(() => {
    return getChangedEmployees().length > 0;
  }, [getChangedEmployees]);

  // Report dirty state to parent whenever it changes
  useEffect(() => {
    onDirtyChange?.(isDirty());
  }, [scoringState, isDirty, onDirtyChange]);

  const [saving, setSaving] = useState(false);

  const performSave = useCallback(async () => {
    const changed = getChangedEmployees();
    if (changed.length === 0) return;

    const updates = changed.map((state) =>
      employeesApi.update(state.employeeId, {
        managerId: state.managerId,
        isScoring: state.included,
        notifyManager: state.notifyManager,
        externalAgentId: state.externalAgentId,
        externalMessagingId: state.externalMessagingId,
      })
    );

    const updatedEmployees = await Promise.all(updates);

    setEmployees((prev) =>
      prev.map((emp) => {
        const updated = updatedEmployees.find((u) => u.id === emp.id);
        return updated ?? emp;
      })
    );
  }, [getChangedEmployees]);

  useImperativeHandle(ref, () => ({
    save: performSave,
    isDirty,
  }), [performSave, isDirty]);

  const handleInternalSave = useCallback(async () => {
    setSaving(true);
    try {
      await performSave();
      onSave?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save employee changes");
    } finally {
      setSaving(false);
    }
  }, [performSave, onSave]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const data = await employeesApi.getAll();
        // Sort by name alphabetically
        const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
        setEmployees(sortedData);
        initializeScoringState(sortedData);
      } catch (_err) {
        setError("Failed to load employees");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [initializeScoringState]);

  useEffect(() => {
    const fetchExternalData = async () => {
      const [agentsResult, messagingResult] = await Promise.allSettled([
        externalAgentsApi.getAll(),
        employeesApi.getMessagingUsers(),
      ]);
      if (agentsResult.status === "fulfilled") setExternalAgents(agentsResult.value);
      if (messagingResult.status === "fulfilled") setMessagingUsers(messagingResult.value);
      setExternalLoading(false);
    };

    fetchExternalData();
  }, []);

  // Auto-match by email: when external lists finish loading, pre-select any exact email matches
  // for employees that have no assigned agent/messaging user yet.
  // Only runs once to avoid overriding manual user changes.
  const autoMatchedRef = React.useRef(false);
  useEffect(() => {
    if (externalLoading) return;
    if (employees.length === 0) return;
    if (autoMatchedRef.current) return;
    autoMatchedRef.current = true;

    setScoringState((prev) =>
      prev.map((state) => {
        const employee = employees.find((e) => e.id === state.employeeId);
        if (!employee) return state;

        const matchedAgent =
          state.externalAgentId === null
            ? externalAgents.find((a) => a.email === employee.email)
            : undefined;

        const matchedMessagingUser =
          state.externalMessagingId === null
            ? messagingUsers.find((u) => u.email === employee.email)
            : undefined;

        if (!matchedAgent && !matchedMessagingUser) return state;

        return {
          ...state,
          externalAgentId: matchedAgent ? matchedAgent.id : state.externalAgentId,
          externalMessagingId: matchedMessagingUser ? matchedMessagingUser.id : state.externalMessagingId,
        };
      })
    );
  }, [externalLoading, externalAgents, messagingUsers, employees]);

  const handleResync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const [data, agentsResult, messagingResult] = await Promise.all([
        employeesApi.sync(),
        externalAgentsApi.getAll(),
        employeesApi.getMessagingUsers(),
      ]);
      // Sort by name alphabetically
      const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
      setEmployees(sortedData);
      initializeScoringState(sortedData);
      setExternalAgents(agentsResult);
      setMessagingUsers(messagingResult);
    } catch (_err) {
      setError("Failed to refresh employees");
    } finally {
      setSyncing(false);
    }
  };

  const handleManagerChange = useCallback(
    (employeeId: string, managerId: string | null) => {
      setScoringState((prev) =>
        prev.map((state) =>
          state.employeeId === employeeId
            ? { ...state, managerId, notifyManager: managerId === null ? false : state.notifyManager }
            : state
        )
      );
    },
    []
  );

  const handleIncludedChange = useCallback(
    (employeeId: string, included: boolean) => {
      setScoringState((prev) =>
        prev.map((state) =>
          state.employeeId === employeeId ? { ...state, included } : state
        )
      );
    },
    []
  );

  const handleNotifyManagerChange = useCallback(
    (employeeId: string, notifyManager: boolean) => {
      setScoringState((prev) =>
        prev.map((state) =>
          state.employeeId === employeeId ? { ...state, notifyManager } : state
        )
      );
    },
    []
  );

  const handleExternalAgentChange = useCallback(
    (employeeId: string, externalAgentId: string | null) => {
      setScoringState((prev) =>
        prev.map((state) => {
          if (state.employeeId === employeeId) return { ...state, externalAgentId };
          if (externalAgentId && state.externalAgentId === externalAgentId) return { ...state, externalAgentId: null };
          return state;
        })
      );
    },
    []
  );

  const handleExternalMessagingChange = useCallback(
    (employeeId: string, externalMessagingId: string | null) => {
      setScoringState((prev) =>
        prev.map((state) => {
          if (state.employeeId === employeeId) return { ...state, externalMessagingId };
          if (externalMessagingId && state.externalMessagingId === externalMessagingId) return { ...state, externalMessagingId: null };
          return state;
        })
      );
    },
    []
  );

  const getScoringStateForEmployee = (employeeId: string) => {
    return scoringState.find((state) => state.employeeId === employeeId);
  };

  // Report included count to parent whenever it changes
  useEffect(() => {
    const includedCount = scoringState.filter((state) => state.included).length;
    onIncludedCountChange?.(includedCount);
  }, [scoringState, onIncludedCountChange]);

  const assignedAgentIds = useMemo(() => {
    const map = new Map<string, string>();
    for (const state of scoringState) {
      if (state.externalAgentId) map.set(state.externalAgentId, state.employeeId);
    }
    return map;
  }, [scoringState]);

  const assignedMessagingIds = useMemo(() => {
    const map = new Map<string, string>();
    for (const state of scoringState) {
      if (state.externalMessagingId) map.set(state.externalMessagingId, state.employeeId);
    }
    return map;
  }, [scoringState]);

  if (loading) {
    return (
      <div className="space-y-2">
        <EmployeeListHeader />
        <div className="grid gap-2">
          <EmployeeCardSkeleton />
          <EmployeeCardSkeleton />
          <EmployeeCardSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive px-4 py-3 rounded-md">
        <Text variant="sm" className="text-destructive">{error}</Text>
      </div>
    );
  }

  if (employees.length === 0) {
    return (
      <Empty className="border">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </EmptyMedia>
          <EmptyTitle>No employees found</EmptyTitle>
          <EmptyDescription>
            No employees have been imported yet.
          </EmptyDescription>
          <Button onClick={handleResync} disabled={syncing} className="mt-4">
            {syncing ? "Refreshing..." : "Refresh Employees"}
          </Button>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="space-y-2">
      <EmployeeListHeader />
      <div className="grid gap-2">
        {employees.map((employee) => {
          const state = getScoringStateForEmployee(employee.id);
          return (
            <EmployeeCard
              key={employee.id}
              employee={employee}
              employees={employees}
              managerId={state !== undefined ? state.managerId : employee.managerId}
              included={state?.included ?? employee.isScoring}
              notifyManager={state?.notifyManager ?? employee.notifyManager}
              externalAgentId={state !== undefined ? state.externalAgentId : employee.externalAgentId}
              externalMessagingId={state !== undefined ? state.externalMessagingId : employee.externalMessagingId}
              externalAgents={externalAgents}
              messagingUsers={messagingUsers}
              externalLoading={externalLoading}
              assignedAgentIds={assignedAgentIds}
              assignedMessagingIds={assignedMessagingIds}
              externalAgentDirty={state?.externalAgentId !== employee.externalAgentId}
              externalMessagingDirty={state?.externalMessagingId !== employee.externalMessagingId}
              onManagerChange={handleManagerChange}
              onIncludedChange={handleIncludedChange}
              onNotifyManagerChange={handleNotifyManagerChange}
              onExternalAgentChange={handleExternalAgentChange}
              onExternalMessagingChange={handleExternalMessagingChange}
            />
          );
        })}
      </div>
      {(!hideRefresh || onSave) && (
        <div className="flex justify-between gap-3 pt-2">
          {onBack ? (
            <Button variant="outline" onClick={onBack} disabled={saving}>
              Back
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-3">
            {!hideRefresh && (
              <Button onClick={handleResync} disabled={syncing} variant="outline">
                {syncing ? "Refreshing..." : "Refresh Employees"}
              </Button>
            )}
            {onSave && (
              <Button onClick={handleInternalSave} disabled={saving || !isDirty()}>
                {saving ? "Saving..." : saveLabel}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

EmployeeList.displayName = "EmployeeList";

export default EmployeeList;
